import { merge } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import uniq from 'lodash/uniq';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import getObjectValues from 'lodash/values';
import { listen } from 'commons/observable';
import { getFieldValues, setFieldValues } from 'commons/utils/fields';
import { escapeHtmlContent } from 'commons/utils/sanitize';
import {
  setQueryParams,
  getQueryParamsByKeys,
  removeQueryParams,
  removeQueryParamWithValues,
  hasQueryParam,
  getURLResource,
} from 'commons/utils/uri';
import { listenLocationChange } from 'commons/utils/location';
import bindSearchFilter from 'commons/utils/filter_list';
import { bindPopovers } from 'commons/bindings/popover';

function getCustomConditionChip(id, name, label, value = label) {
  return `
		<div class="custom-chip naked-chip chip-with-button">
      <input class="facet_checkbox"  type="checkbox" id="${id}" name="${name}" value="${value}" checked>
      <label for="${id}">${label} <button class="remove-custom-condition"><i class="fas fa-times"></i></button></label>
    </div>
	`;
}

function getAdvancedSelectedFacet({ label, name, id, facetId, value, type }) {
  return `
		<div class="custom-chip naked-chip chip-with-button">
      <input class="facet_checkbox" type="checkbox" id="${id}" data-name="${name}" data-facet-id="${facetId}" data-type="${type}" value="${value}" disabled checked>
      <label for="${id}">${label} <button class="remove-facet"><i class="fas fa-times"></i></button></label>
    </div>
	`;
}

function getAdvancedAppliedFacet({ label, name, id, facetId, value, type }) {
  return `
		<div class="custom-chip naked-chip chip-with-button">
      <input class="facet_checkbox" type="checkbox" id="${id}" data-name="${name}" data-facet-id="${facetId}" data-type="${type}" value="${value}" disabled checked>
      <label class="cursor-default" for="${id}">${label} <button class="remove-facet"><i class="fas fa-times"></i></button></label>
    </div>
	`;
}

function getFacetFieldsConfig(selector) {
  const facetConfigs = $(selector).map(function () {
    const { type, tagName, name } = this;
    return {
      type,
      tagName,
      name,
    };
  });

  return uniqWith(facetConfigs, isEqual);
}

function getFacetFieldNames(selector) {
  const facetFieldNames = getFacetFieldsConfig(selector).map(
    ({ name }) => name
  );
  return uniq(facetFieldNames);
}

function getAppliedFacetFields(selector, facetNames) {
  const appliedFacetValues = getQueryParamsByKeys(facetNames);

  const fields = $(selector).filter(function () {
    const $field = this;

    const { name, value } = $field;
    const facetValues = appliedFacetValues[name];

    return (
      facetValues &&
      ((Array.isArray(facetValues) && facetValues.includes(value)) ||
        facetValues === value)
    );
  });

  return fields;
}

function getNonAdvancedFacetFieldSelectors() {
  return ['input[type="checkbox"]', 'input[type="radio"]', 'select']
    .map((fieldType) => ` .trial-facets ${fieldType}`)
    .join(',');
}

function getAdvancedFacetFieldSelectors() {
  return ['input[type="checkbox"]', 'input[type="radio"]']
    .map((fieldType) => ` .trial-facets-advanced ${fieldType}`)
    .join(',');
}

function getNonAdvancedFacetNames() {
  const nonAdvancedFacetSelector = getNonAdvancedFacetFieldSelectors();
  return getFacetFieldNames(nonAdvancedFacetSelector);
}

function getAdvancedFacetNames() {
  const advancedFacetSelectors = getAdvancedFacetFieldSelectors();
  return getFacetFieldNames(advancedFacetSelectors);
}

function clearFacets(facetSelectors, facetNames = []) {
  $(facetSelectors).each(function () {
    const $elem = $(this);
    const fieldType = this.type;
    const fieldTagName = this.tagName.toLowerCase();

    if (fieldType === 'checkbox' || fieldType === 'radio') {
      $elem.prop('checked', false);
    } else if (fieldTagName === 'select') {
      const defaultValue = $elem.find('option').first().val();
      $elem.selectpicker('val', defaultValue);
    }
  });
  removeQueryParams(facetNames, { action: 'replace' });
}

function clearAdvancedFacets() {
  const advancedFacetsNames = getAdvancedFacetNames();
  const advancedFacetSelectors = getAdvancedFacetFieldSelectors();

  clearFacets(advancedFacetSelectors, advancedFacetsNames);
}

function clearNonAdvancedFacets() {
  const nonAdvancedFacetNames = getNonAdvancedFacetNames();
  const nonAdvancedFacetSelectors = getNonAdvancedFacetFieldSelectors();

  clearFacets(nonAdvancedFacetSelectors, nonAdvancedFacetNames);
}

function clearAllFacets() {
  clearAdvancedFacets();
  clearNonAdvancedFacets();
}

function isFiltersApplied() {
  const advancedFacetNames = getAdvancedFacetNames();
  const nonAdvancedFacetNames = getNonAdvancedFacetNames();
  const totalFacetNames = [...advancedFacetNames, ...nonAdvancedFacetNames];
  const totalFacetValues = getQueryParamsByKeys(totalFacetNames);

  return getObjectValues(totalFacetValues).some((value) => !!value);
}

function setClearAllFacets() {
  const $clearAllFilters = $('#clear-all-facets');
  const filtersApplied = isFiltersApplied();

  filtersApplied
    ? $clearAllFilters.removeClass('d-none')
    : $clearAllFilters.addClass('d-none');
}

function setAdvancedSelectedFilters() {
  const advancedFacetSelectors = getAdvancedFacetFieldSelectors();
  const advancedFacetsNames = getAdvancedFacetNames();
  const existingAppliedFacets = getAppliedFacetFields(
    advancedFacetSelectors,
    advancedFacetsNames
  );

  clearAdvancedSelectedFilters();

  existingAppliedFacets.each(function () {
    const $facet = this;
    const { type, name, id, value, checked } = $facet;
    updateAdvancedSelectedFilters({ type, name, id, value, checked });
  });
}

function setAdvancedAppliedFilters() {
  const $appliedAdvancedFiltersRoot = $('#applied-advanced-filters-root');
  const $appliedAdvancedFilters = $('#applied-advanced-filters');
  const advancedFacetSelectors = getAdvancedFacetFieldSelectors();
  const advancedFacetsNames = getAdvancedFacetNames();
  const $appliedFacets = getAppliedFacetFields(
    advancedFacetSelectors,
    advancedFacetsNames
  );

  $appliedAdvancedFilters.children().remove();

  $appliedFacets.each(function () {
    const $facet = this;
    const { type, name, id: facetId, value, checked } = $facet;

    const selectedFilterId = `adv_applied_${facetId}`;
    // Get input label without count
    const labelText = $(`label[for=${facetId}]`).text();
    const label = getAdvancedFacetLabel(labelText);

    const appliedFacet = getAdvancedAppliedFacet({
      label,
      name,
      id: selectedFilterId,
      facetId,
      type,
      value,
    });

    $appliedAdvancedFilters.append(appliedFacet);
  });

  if ($appliedAdvancedFilters.children().length === 0) {
    $appliedAdvancedFiltersRoot.addClass('d-none');
  } else {
    $appliedAdvancedFiltersRoot.removeClass('d-none');
  }
}

function clearAdvancedSelectedFilters() {
  const $selectedAdvancedFilters = $('#selected-advanced-filters');
  $selectedAdvancedFilters.children().remove();
  toggleAdvancedSelectedFiltersBorder();
}

function toggleAdvancedSelectedFiltersBorder() {
  const $selectedAdvancedFilters = $('#selected-advanced-filters');

  // Toggle border-top
  $selectedAdvancedFilters.children().length
    ? $selectedAdvancedFilters.addClass('border-top pt-3 mt-3')
    : $selectedAdvancedFilters.removeClass('border-top pt-3 mt-3');
}

function removeFacet({ type, name, value }) {
  if (type === 'checkbox' || type === 'radio') {
    const selector = `input[type=${type}][name=${name}][value="${value}"]`;
    const $facetInput = $(selector);
    $facetInput.prop('checked', false);
    $facetInput.change();
  }
}

function getAdvancedFacetLabel(labelText = '') {
  const labelRegex = /^(.*?)(\s\(.*\))?$/;
  const [, label] = labelRegex.exec(labelText) || [];

  return label;
}

function updateAdvancedSelectedFilters({
  type,
  name,
  id: facetId,
  value,
  checked,
}) {
  const $selectedAdvancedFilters = $('#selected-advanced-filters');
  const selectedFilterId = `adv_selected_${facetId}`;
  // Get input label without count
  const labelText = $(`label[for=${facetId}]`).text();
  const label = getAdvancedFacetLabel(labelText);

  const selectedFilter = getAdvancedSelectedFacet({
    label,
    name,
    id: selectedFilterId,
    facetId,
    type,
    value,
  });

  function addSelectedFilter() {
    $selectedAdvancedFilters.append(selectedFilter);
  }

  function removeSelectedFilter() {
    if (type === 'checkbox') {
      $selectedAdvancedFilters.find(`#${selectedFilterId}`).parent().remove();
    } else if (type === 'radio') {
      $selectedAdvancedFilters
        .find(`input[data-name=${name}]`)
        .parent()
        .remove();
    }
  }

  if (type === 'checkbox') {
    checked ? addSelectedFilter() : removeSelectedFilter();
  } else if (type === 'radio') {
    // Remove previous selected if exists
    removeSelectedFilter();
    checked && addSelectedFilter();
  }

  toggleAdvancedSelectedFiltersBorder();
}

function handleConditionExpandCollapse(e) {
  e.preventDefault();

  const $expandConditionsBtn = $(e.target);
  const shouldExpand = !$expandConditionsBtn.data('expanded');

  function hideConditionLabels() {
    $expandConditionsBtn.siblings().each((index, chip) => {
      const $conditionLabel = $(chip);
      const isChecked = $conditionLabel.find('input').prop('checked');
      if (index > 4 && !isChecked) {
        $conditionLabel.addClass('d-none');
      }
    });
  }

  function showConditionLabels() {
    $expandConditionsBtn.siblings('.d-none').removeClass('d-none');
  }

  console.log({ shouldExpand });

  shouldExpand ? showConditionLabels() : hideConditionLabels();

  $expandConditionsBtn.data('expanded', shouldExpand);
  $expandConditionsBtn.text(shouldExpand ? 'less' : 'more');
  // $expandConditionsBtn.addClass('d-none');
}

function bindEventHandlers() {
  const clearAllFacetsBtn = document.getElementById('clear-all-facets');
  listen(clearAllFacetsBtn, 'click').subscribe((e) => {
    e.preventDefault();
    clearAllFacets();
  });
}

function bindConditionFacets() {
  const $showMoreConditionsBtn = $('.more-condition-facets');
  const $addCustomConditionInput = $('.add-custom-condition');
  const $addCustomConditionBtn = $('.add-custom-condition + .chip-action');
  const $conditionFacetsWrapper = $('#trial-facets-wrapper');

  function addNewCondition(input) {
    const $customConditionInput = $(input);
    const $wrapper = $($customConditionInput.closest('.custom-chip'));

    const facetType = $customConditionInput.data('name');
    const value = $customConditionInput.val().trim();
    const sanitizedValue = escapeHtmlContent(value);

    const totalFacets = $wrapper.siblings('.custom-chip').length;
    const customChipId = `${facetType}_${totalFacets + 1}`;
    const customChip = getCustomConditionChip(
      customChipId,
      facetType,
      sanitizedValue
    );

    const $customChipRef = $(customChip).insertBefore($wrapper);
    $customChipRef.find('input').trigger('change');
    $customConditionInput.val('');
  }

  // Show more conditions when clicking more on looking for/had conditions
  listen($showMoreConditionsBtn, 'click').subscribe(
    this.handleConditionExpandCollapse
  );

  // Add new custom condition for looking for/ have_had on blur or pressing enter
  const conditionInputKeyup$ = listen($addCustomConditionInput, 'keyup');
  const conditionInputBtn$ = listen($addCustomConditionBtn, 'click');

  merge(conditionInputKeyup$, conditionInputBtn$)
    .pipe(
      filter(
        (e) => e.type === 'click' || (e.type === 'keyup' && e.keyCode === 13)
      ),
      map((e) =>
        e.type === 'click'
          ? $(e.target).closest('.input-chip').find('.add-custom-condition')[0]
          : e.target
      ),
      filter((input) => !!input.value.toString().trim())
    )
    .subscribe(addNewCondition);

  $conditionFacetsWrapper.on('click', '.remove-custom-condition', function (e) {
    const $facetLabel = $(this).closest('label');
    const $facetInput = $facetLabel.siblings('input');
    const facetName = $facetInput.attr('name');
    const facetValue = $facetInput.val();

    removeQueryParamWithValues(
      { [facetName]: facetValue },
      { action: 'replace' }
    );

    $facetInput.parent().remove();
  });
}

function bindTrialFacets() {
  const self = this;
  const $trialFacets = $('.trial-facets');

  listen($trialFacets, 'change')
    .pipe(
      map((e) => e.target),
      filter(
        ({ type, tagName }) =>
          type === 'checkbox' ||
          tagName.toLowerCase() === 'select' ||
          type === 'radio'
      ),
      map(({ name, type, tagName }) => ({
        name,
        type,
        tagName,
      }))
    )
    .subscribe(({ name, type, tagName }) => {
      const fieldValues = getFieldValues({
        tagName,
        type,
        name,
      });

      const facetValues = {
        [name]: fieldValues,
      };

      self.handleStateChange(facetValues);
    });
}

function bindAdvancedTrialFacets() {
  const self = this;
  const advancedFacetsSelectors = getAdvancedFacetFieldSelectors();
  const $advancedFilterModel = $('#advanced-filter-modal');
  const $advancedFiltersApply = $('#apply-advanced-filters');
  const $advancedFiltersReset = $('#reset-advanced-filters');
  const $advancedFacets = $(advancedFacetsSelectors);
  const $selectedAdvancedFilters = $('#selected-advanced-filters');
  const $appliedAdvancedFilters = $('#applied-advanced-filters');

  const $facetTabs = $('#advanced-facet-tabs');

  // Bind advanced facets   search filter
  bindSearchFilter();

  listen($advancedFilterModel, 'hide.bs.modal').subscribe((e) => {
    const applyFacetsClicked =
      $advancedFiltersApply[0] === document.activeElement;
    const resetFacetsClicked =
      $advancedFiltersApply[0] === document.activeElement;

    if (!applyFacetsClicked && !resetFacetsClicked) {
      const advancedFacetSelectors = getAdvancedFacetFieldSelectors();
      const advancedFacetsNames = getAdvancedFacetNames();
      const advancedFacetsConfig = getFacetFieldsConfig(advancedFacetSelectors);

      const facetValues = getQueryParamsByKeys(advancedFacetsNames);

      const facetsConfigWithValues = advancedFacetsConfig.map((config) => ({
        ...config,
        values: facetValues[config.name],
      }));

      setFieldValues(facetsConfigWithValues);
      setAdvancedSelectedFilters();
    }
  });

  listen($advancedFiltersApply, 'click').subscribe((_) => {
    const facets = {};
    const advancedFacetSelectors = getAdvancedFacetFieldSelectors();
    const fieldConfigs = getFacetFieldsConfig(advancedFacetSelectors);

    fieldConfigs.forEach(({ tagName, type, name }) => {
      facets[name] = getFieldValues({ tagName, type, name });
    });

    self.handleStateChange(facets);
    $advancedFilterModel.modal('hide');
  });

  listen($advancedFiltersReset, 'click').subscribe((_) => {
    clearAdvancedFacets();
    $advancedFilterModel.modal('hide');
  });

  listen($advancedFacets, 'change')
    .pipe(
      map((e) => e.target),
      map(({ name, type, value, checked, id }) => ({
        name,
        id,
        type,
        value,
        checked,
      }))
    )
    .subscribe(updateAdvancedSelectedFilters);

  listen($selectedAdvancedFilters, 'click').subscribe((e) => {
    const $facetLabel = $(e.target);
    const facetName = $facetLabel.siblings('input').data('name');
    const facetId = $facetLabel.siblings('input').data('facet-id');
    const $facetInput = document.getElementById(facetId);
    const facetTabName = `${facetName}-tab`;
    const $selectedTab = $facetTabs.find(`a[id=${facetTabName}]`);
    const $activeTab = $facetTabs.find('a.active');

    if ($activeTab.get(0) !== $selectedTab.get(0)) {
      $selectedTab.one('shown.bs.tab', function (e) {
        $facetInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      $selectedTab.tab('show');
    } else {
      $facetInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  $selectedAdvancedFilters.on('click', '.remove-facet', function ({ target }) {
    const $facetLabel = $(target).closest('label');
    const $facetInput = $facetLabel.siblings('input');
    const name = $facetInput.data('name');
    const type = $facetInput.data('type');
    const value = $facetInput.val();

    removeFacet({ name, type, value });
  });

  $appliedAdvancedFilters.on('click', '.remove-facet', function ({ target }) {
    const $facetLabel = $(target).closest('label');
    const $facetInput = $facetLabel.siblings('input');
    const name = $facetInput.data('name');
    const type = $facetInput.data('type');
    const value = $facetInput.val();

    removeFacet({ type, name, value });
    removeQueryParamWithValues({ [name]: value }, { action: 'replace' });
  });
}

function bindLocationChange() {
  const self = this;
  listenLocationChange().subscribe((_) => {
    setAdvancedSelectedFilters();
    setAdvancedAppliedFilters();
    setClearAllFacets();
    self.fetchTrialResults();
  });
}

function handleStateChange(values = {}) {
  // Reset pagination if on different page
  if (hasQueryParam('page')) {
    removeQueryParams('page', { action: 'replace' });
  }
  setQueryParams(values, { action: 'replace' });
}

function showTrialsLoader() {
  const $loader = $('#trialsLoader');
  $loader.show();
}

function hideTrialsLoader() {
  const $loader = $('#trialsLoader');
  $loader.hide();
}

function fetchTrialResults() {
  const self = this;
  const url = getURLResource();
  if (self.ajaxXHR) {
    self.ajaxXHR.abort();
    self.ajaxXHR = null;
  }
  self.showTrialsLoader();

  self.ajaxXHR = $.ajax({
    url,
  })
    .done((response) => {
      //todo handle edge case on ajax
      $('#search_result').html(response);
      $('.select-custom').selectpicker({
        style: 'select-custom-btn',
      });
      $('[data-toggle="tooltip"]').tooltip();
      self.hideTrialsLoader();
      bindPopovers();
    })
    .fail((error) => {
      console.log('Error:', error);
      self.hideTrialsLoader();
    });
}

function initTrialFacets() {
  const advancedFacetsSelectors = getAdvancedFacetFieldSelectors();
  const nonAdvancedFacetsSelectors = getNonAdvancedFacetFieldSelectors();
  const advancedFacetNames = getAdvancedFacetNames();
  const nonAdvancedFacetNames = getNonAdvancedFacetNames();

  const totalFacetNames = [...advancedFacetNames, ...nonAdvancedFacetNames];
  const totalFacetSelectors = [
    advancedFacetsSelectors,
    nonAdvancedFacetsSelectors,
  ].join(',');

  const totalFacetsConfigs = getFacetFieldsConfig(totalFacetSelectors);
  const totalFacetValues = getQueryParamsByKeys(totalFacetNames);

  const totalFacetsWithValues = totalFacetsConfigs.map((facet) => ({
    ...facet,
    values: totalFacetValues[facet.name],
  }));

  setFieldValues(totalFacetsWithValues);
  setAdvancedAppliedFilters();
  setAdvancedSelectedFilters();
  setClearAllFacets();
}

function init() {
  this.initTrialFacets();
  this.bindEventHandlers();
  this.bindConditionFacets();
  this.bindTrialFacets();
  this.bindAdvancedTrialFacets();
  this.bindLocationChange();
}

function TrialFacetsViewModel() {
  const self = this;

  self.ajaxXHR = null;

  self.init = init.bind(self);
  self.initTrialFacets = initTrialFacets.bind(self);
  self.bindEventHandlers = bindEventHandlers.bind(self);
  self.bindConditionFacets = bindConditionFacets.bind(self);
  self.bindTrialFacets = bindTrialFacets.bind(self);
  self.bindAdvancedTrialFacets = bindAdvancedTrialFacets.bind(self);
  self.bindLocationChange = bindLocationChange.bind(self);
  self.handleStateChange = handleStateChange.bind(self);
  self.fetchTrialResults = fetchTrialResults.bind(self);
  self.showTrialsLoader = showTrialsLoader.bind(self);
  self.hideTrialsLoader = hideTrialsLoader.bind(self);
  self.handleConditionExpandCollapse = handleConditionExpandCollapse.bind(self);
}

export default TrialFacetsViewModel;
